import { useEffect, useRef } from 'react';
import { PACKAGE_STATUS_ORDERED, PACKAGE_STATUS_TEMP, PACKAGE_TEMP_STALE_MS } from '../../../functions/src/api/constants';
import { isPackageTempStale } from '../../../functions/src/common/packageOrderability';
import type { UserMealPackageTemp } from '../../../functions/src/types';
import type { DataSnapshot } from '../../api/firebase';

/** Remove TEMP / ORDERED package rows older than {@link PACKAGE_TEMP_STALE_MS} when the Packages screen loads. */
export function useClearStalePackageTemps(
    tempDb: DataSnapshot | null | undefined,
    onCleared: () => void,
    nowMs?: number,
): void {
    const onClearedRef = useRef(onCleared);
    onClearedRef.current = onCleared;
    const nowMsRef = useRef(nowMs);
    nowMsRef.current = nowMs;
    const clearingRef = useRef(false);
    /** Server clock became available — do not depend on `nowMs` itself (changes every render). */
    const serverNowReady = nowMs !== undefined;

    useEffect(() => {
        if (!tempDb || !serverNowReady || clearingRef.current) {
            return;
        }
        const evaluatedNowMs = nowMsRef.current;
        if (evaluatedNowMs === undefined) {
            return;
        }
        const removals: Promise<void>[] = [];
        tempDb.forEach((child) => {
            if (child.key === '_fulfillment') {
                return;
            }
            const status = child.child('status').val();
            if (status !== PACKAGE_STATUS_TEMP && status !== PACKAGE_STATUS_ORDERED) {
                return;
            }
            const val = child.val() as UserMealPackageTemp | null;
            if (!val || !isPackageTempStale(val, PACKAGE_TEMP_STALE_MS, evaluatedNowMs)) {
                return;
            }
            removals.push(child.ref.remove().then(() => undefined));
        });
        if (removals.length === 0) {
            return;
        }
        clearingRef.current = true;
        void Promise.all(removals)
            .then(() => onClearedRef.current())
            .catch((err) => console.error('Error clearing stale package temps:', err))
            .finally(() => {
                clearingRef.current = false;
            });
    }, [tempDb, serverNowReady]);
}
